import { Toast } from '@byecode/ui'
import { getFlatAllBlock } from '@lighthouse/block'
import type { BlockAbstract, ContainerBlockAbstract } from '@lighthouse/core'
import { BlockType, DIRECTION } from '@lighthouse/core'
import {
    blockHasSomeChildren,
    createBlockFinder,
    deepClearSyncBlock,
    findBlockById,
    findParentBlockById,
    getCurrentBlockChildren,
    isButtonBlock,
    isContainerBlock,
    isCustomChildren,
    isCustomViewBlock,
    isFormContainerBlock,
    isFormContainerChildren,
    isIconBlock,
    isTextBlock,
    PAGE_LAYER_ID,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import { getBrowser, nanoid } from '@lighthouse/tools'
import { current, original } from 'immer'
import { useAtomCallback } from 'jotai/utils'
import { clone } from 'rambda'
import { useHotkeys } from 'react-hotkeys-hook'

import { mainBreakPointAtom, syncComponentsAtom } from '@/atoms/application/state'
import { createBlockAtom, initContainerRuntimeStateAtom, removeBlockAtom } from '@/atoms/page/action'
import {
    blockCreatingListAtom,
    blocksAtom,
    containerRuntimeStateAtomFamily,
    pageBlocksAtom,
    pageInternalStateAtomFamily
} from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import { AsideType } from '@/atoms/page/types'
import { copiedBoxSelectionNodesSnapshotAtom } from '@/atoms/workSpace/state'
import { generateContainerBlock } from '@/constants/Block/generate/container'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import * as srv from '@/services'
import { NODE_UPDATE_ACTION, pageUndoRedoController } from '@/utils/undoRedo/page/controller'

import { useNodeToolbarActions } from './useNodeToolbarActions'
import { copyBlock, includeNotAllowedBlock, transformBlock2SyncComponents } from './utils'

type Configure = {
    ref: React.RefObject<HTMLDivElement | null>
    enabled?: boolean
}

/** 选中的节点操作 */
export const useSelectionNodesActions = ({ ref, enabled }: Configure) => {
    const { pageId } = useCurrentPageContext()
    const stackId = useCurrentStackIdContext()
    const mainBreakPoint = useAtomData(mainBreakPointAtom)

    const { run: createBlock } = useAtomAction(createBlockAtom)
    const { run: removeBlock } = useAtomAction(removeBlockAtom)

    const actions = useNodeToolbarActions({ stackId, pageId })

    const handleCopy = useAtomCallback((get, set, copyIds: NodeIdWithScope[]) => {
        const blocks = get(blocksAtom)[pageId]
        const syncComponents = get(syncComponentsAtom)
        // 使用 createBlockFinder 优化批量查找性能
        const blockFinder = createBlockFinder(blocks ?? [], syncComponents)
        const isFloatBoxBlock = copyIds?.some(id => blockFinder.find(id)?.type === BlockType.floatBox)
        if (!blocks || isFloatBoxBlock) {
            return
        }
        set(copiedBoxSelectionNodesSnapshotAtom, { copiedIds: [...copyIds], blocks: clone(blocks), syncComponents: clone(syncComponents) })
        Toast.success(`已复制${copyIds.length}个组件`)
    })

    const handleCut = useAtomCallback((get, set, ids: NodeIdWithScope[]) => {
        const blocks = get(blocksAtom)[pageId]
        const syncComponents = get(syncComponentsAtom)
        // 使用 createBlockFinder 优化批量查找性能
        const blockFinder = createBlockFinder(blocks ?? [], syncComponents)
        const isFloatBoxBlock = ids?.some(id => blockFinder.find(id)?.type === BlockType.floatBox)
        if (!blocks || isFloatBoxBlock) {
            return
        }

        actions.onRemove(ids)
        set(copiedBoxSelectionNodesSnapshotAtom, { copiedIds: [...ids], blocks: clone(blocks), syncComponents: clone(syncComponents) })
        Toast.success(`已复制${ids.length}个组件`)
    })

    const handlePaste = useAtomCallback(
        async (
            get,
            set,
            snapshot: {
                copiedIds: NodeIdWithScope[]
                blocks: BlockAbstract[]
                syncComponents: ContainerBlockAbstract[]
            }
        ) => {
            const { asideType, selectedNodes } = get(pageInternalStateAtomFamily(stackId))
            // 获取容器状态的函数
            const getContainerState = (containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

            const pageBlocks = get(blocksAtom)[pageId] || []
            const syncComponents = get(syncComponentsAtom)

            // 使用 createBlockFinder 优化批量查找性能
            const snapshotBlockFinder = createBlockFinder(snapshot.blocks, snapshot.syncComponents)
            const pageBlockFinder = createBlockFinder(pageBlocks, syncComponents)

            const pasteBlocks = snapshot.copiedIds.map(id => snapshotBlockFinder.find(id)).filter(Boolean) as BlockAbstract[]

            const blocksUndoRedoInfo: { createdBlocks?: BlockAbstract[]; prevBlocks?: BlockAbstract[]; nextBlocks?: BlockAbstract[] } = {}
            const syncComponentsUndoRedoInfo: {
                createdBlocks?: BlockAbstract[]
                prevBlocks?: ContainerBlockAbstract[]
                nextBlocks?: ContainerBlockAbstract[]
            } = {}

            let isPaste2SyncComponents = false

            if (asideType === AsideType.PAGE) {
                set(blocksAtom, draft => {
                    const draftBlocks = draft[pageId]
                    if (!draftBlocks) {
                        return
                    }

                    blocksUndoRedoInfo.createdBlocks = copyBlock(
                        snapshot.copiedIds
                            .map(id => {
                                const pageBlock = findBlockById(id.id, snapshot.blocks)
                                const actualBlock = pageBlock ? undefined : snapshotBlockFinder.find(id)
                                if (!pageBlock && !actualBlock) {
                                    return null
                                }

                                if (pageBlock?.synchronousId && isContainerBlock(pageBlock)) {
                                    pageBlock.children = []
                                }

                                // 同步组件内子元素，复制后删除同步标识
                                if (actualBlock?.isLeafSynchronous) {
                                    deepClearSyncBlock(actualBlock)
                                    return actualBlock
                                }

                                return pageBlock
                            })
                            .filter(Boolean) as BlockAbstract[]
                    )

                    set(blockCreatingListAtom, s => [...s, ...getFlatAllBlock(blocksUndoRedoInfo.createdBlocks!).map(item => item.id)])

                    draftBlocks.push(...blocksUndoRedoInfo.createdBlocks)
                    blocksUndoRedoInfo.prevBlocks = original(draftBlocks)
                    blocksUndoRedoInfo.nextBlocks = current(draftBlocks)
                })
            } else if (asideType === AsideType.BLOCK) {
                if (!selectedNodes || selectedNodes.length !== 1) {
                    return
                }

                const selectedNode = selectedNodes[0]
                const actualTargetBlock = pageBlockFinder.find(selectedNode)
                if (!actualTargetBlock) {
                    return
                }

                isPaste2SyncComponents = !!actualTargetBlock.isMasterSynchronous || !!actualTargetBlock.isLeafSynchronous

                // 粘往同步组件内
                if (isPaste2SyncComponents) {
                    set(syncComponentsAtom, draft => {
                        syncComponentsUndoRedoInfo.createdBlocks = copyBlock(pasteBlocks)
                        if (
                            syncComponentsUndoRedoInfo.createdBlocks.some(
                                item => !checkNotAllowed({ fromBlock: item, toBlock: actualTargetBlock, blocks: draft })
                            )
                        ) {
                            return
                        }
                        if (
                            syncComponentsUndoRedoInfo.createdBlocks.some(item =>
                                blockHasSomeChildren(block => !!block.isMasterSynchronous)(item)
                            )
                        ) {
                            Toast.error('同步组件不能粘贴到同步组件中')
                            return
                        }

                        // 将blocks设置为子同步元素
                        syncComponentsUndoRedoInfo.createdBlocks = syncComponentsUndoRedoInfo.createdBlocks.map(block =>
                            transformBlock2SyncComponents(block, false)
                        )

                        set(blockCreatingListAtom, s => [
                            ...s,
                            ...getFlatAllBlock(syncComponentsUndoRedoInfo.createdBlocks!).map(item => item.id)
                        ])

                        const draftTarget = findBlockById(actualTargetBlock.id, draft)
                        if (!draftTarget) {
                            return
                        }

                        const children = getCurrentBlockChildren(draftTarget, {
                            getContainerState,
                            uniqueContainerId: draftTarget.isMasterSynchronous ? selectedNode.id : `${selectedNode.scope}@${draftTarget.id}`
                        })

                        const isCanPushBlock =
                            children && !(isTextBlock(draftTarget) || isIconBlock(draftTarget) || isButtonBlock(draftTarget))
                        if (isCanPushBlock) {
                            children.push(...syncComponentsUndoRedoInfo.createdBlocks)
                        } else {
                            const parent = findParentBlockById(draftTarget.id, draft)
                            if (!parent) {
                                return
                            }

                            const parentChildren = getCurrentBlockChildren(parent, {
                                getContainerState,
                                uniqueContainerId: parent.isMasterSynchronous ? selectedNode.scope : `${selectedNode.scope}@${parent.id}`
                            })

                            if (!parentChildren) {
                                return
                            }

                            const index = parentChildren.findIndex(item => item.id === draftTarget.id)
                            if (index === -1) {
                                return
                            }

                            parentChildren.splice(index + 1, 0, ...syncComponentsUndoRedoInfo.createdBlocks)
                        }
                        syncComponentsUndoRedoInfo.prevBlocks = original(draft)
                        syncComponentsUndoRedoInfo.nextBlocks = current(draft)
                    })
                } else {
                    set(blocksAtom, draft => {
                        const draftBlocks = draft[pageId]
                        if (!draftBlocks) {
                            return
                        }

                        const draftBlock = findBlockById(selectedNode.id, draftBlocks)
                        if (!draftBlock) {
                            return
                        }

                        blocksUndoRedoInfo.createdBlocks = copyBlock(
                            snapshot.copiedIds
                                .map(id => {
                                    const pageBlock = findBlockById(id.id, snapshot.blocks)
                                    const actualBlock = pageBlock ? undefined : snapshotBlockFinder.find(id)
                                    if (!pageBlock && !actualBlock) {
                                        return null
                                    }

                                    if (pageBlock?.synchronousId && isContainerBlock(pageBlock)) {
                                        pageBlock.children = []
                                    }

                                    // 同步组件内子元素，复制后删除同步标识
                                    if (actualBlock?.isLeafSynchronous) {
                                        deepClearSyncBlock(clone(actualBlock))
                                        return actualBlock
                                    }

                                    return pageBlock
                                })
                                .filter(Boolean) as BlockAbstract[]
                        )

                        if (
                            blocksUndoRedoInfo.createdBlocks.some(
                                item => !checkNotAllowed({ fromBlock: item, toBlock: draftBlock, blocks: draftBlocks })
                            )
                        ) {
                            return
                        }

                        set(blockCreatingListAtom, s => [...s, ...getFlatAllBlock(blocksUndoRedoInfo.createdBlocks!).map(item => item.id)])

                        const children = getCurrentBlockChildren(draftBlock, { getContainerState })
                        const isCanPushBlock =
                            children && !(isTextBlock(draftBlock) || isIconBlock(draftBlock) || isButtonBlock(draftBlock))
                        if (isCanPushBlock) {
                            children.push(...blocksUndoRedoInfo.createdBlocks)
                        } else {
                            const parent = findParentBlockById(draftBlock.id, draftBlocks)
                            const parentChildren = parent
                                ? getCurrentBlockChildren(parent, {
                                      getContainerState
                                  })
                                : draftBlocks

                            if (!parentChildren) {
                                return
                            }

                            const index = parentChildren.findIndex(item => item.id === draftBlock.id)
                            if (index === -1) {
                                return
                            }

                            parentChildren.splice(index + 1, 0, ...blocksUndoRedoInfo.createdBlocks)
                        }

                        blocksUndoRedoInfo.prevBlocks = original(draftBlocks)
                        blocksUndoRedoInfo.nextBlocks = current(draftBlocks)
                    })
                }
            }

            if (isPaste2SyncComponents) {
                if (
                    !syncComponentsUndoRedoInfo.prevBlocks ||
                    !syncComponentsUndoRedoInfo.nextBlocks ||
                    !syncComponentsUndoRedoInfo.createdBlocks
                ) {
                    return
                }

                set(initContainerRuntimeStateAtom, {
                    stackId,
                    blocks: get(pageBlocksAtom(pageId)),
                    syncComponents: syncComponentsUndoRedoInfo.nextBlocks
                })

                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        created: {
                            createdBlocks: syncComponentsUndoRedoInfo.createdBlocks,
                            prevBlocks: syncComponentsUndoRedoInfo.prevBlocks,
                            nextBlocks: syncComponentsUndoRedoInfo.nextBlocks
                        }
                    }
                })

                await srv.createSyncBlock({
                    createBlocks: syncComponentsUndoRedoInfo.createdBlocks,
                    blocks: syncComponentsUndoRedoInfo.nextBlocks
                })

                set(blockCreatingListAtom, s =>
                    s.filter(id => !getFlatAllBlock(syncComponentsUndoRedoInfo.createdBlocks!).some(item => item.id === id))
                )
            } else {
                if (!blocksUndoRedoInfo.createdBlocks || !blocksUndoRedoInfo.prevBlocks || !blocksUndoRedoInfo.nextBlocks) {
                    return
                }

                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        created: {
                            createdBlocks: blocksUndoRedoInfo.createdBlocks,
                            prevBlocks: blocksUndoRedoInfo.prevBlocks,
                            nextBlocks: blocksUndoRedoInfo.nextBlocks
                        }
                    }
                })

                await createBlock({
                    pageId,
                    stackId,
                    createdBlocks: blocksUndoRedoInfo.createdBlocks,
                    blocks: blocksUndoRedoInfo.nextBlocks
                })
                set(blockCreatingListAtom, s =>
                    s.filter(id => !getFlatAllBlock(blocksUndoRedoInfo.createdBlocks!).some(item => item.id === id))
                )
            }
        }
    )

    const handleKeydown = useAtomCallback<void, Parameters<Parameters<typeof useHotkeys>[1]>>((get, set, event, hotkeysEvent) => {
        const copiedBoxSelectionNodesSnapshot = get(copiedBoxSelectionNodesSnapshotAtom)
        const { selectedNodes } = get(pageInternalStateAtomFamily(stackId))

        const isMac = getBrowser().getBrowserInfo().platform === 'Macintosh'

        // 复制
        if (selectedNodes && selectedNodes.length > 0 && (isMac ? hotkeysEvent.key === '⌘+c' : hotkeysEvent.key === 'ctrl+c')) {
            if (
                !ref.current?.contains(event.target as Node) &&
                !document.querySelector(`#${PAGE_LAYER_ID}`)?.contains(event.target as Node)
            ) {
                return
            }
            handleCopy(selectedNodes)
        }

        // 剪切
        if (selectedNodes && selectedNodes.length > 0 && (isMac ? hotkeysEvent.key === '⌘+x' : hotkeysEvent.key === 'ctrl+x')) {
            if (
                !ref.current?.contains(event.target as Node) &&
                !document.querySelector(`#${PAGE_LAYER_ID}`)?.contains(event.target as Node)
            ) {
                return
            }

            handleCut(selectedNodes)
        }

        // 粘贴
        if (copiedBoxSelectionNodesSnapshot.copiedIds.length > 0 && (isMac ? hotkeysEvent.key === '⌘+v' : hotkeysEvent.key === 'ctrl+v')) {
            // TODO: @zjb 为什么加这个限制？  hkl
            if (
                !ref.current?.contains(event.target as Node) &&
                !document.querySelector(`#${PAGE_LAYER_ID}`)?.contains(event.target as Node)
            ) {
                return
            }
            handlePaste(copiedBoxSelectionNodesSnapshot)
        }

        // TODO: @zjb 为什么加这个限制？  hkl
        if (!ref.current?.contains(event.target as Node) && !document.querySelector(`#${PAGE_LAYER_ID}`)?.contains(event.target as Node)) {
            return
        }

        // 删除
        if (
            selectedNodes &&
            selectedNodes.length > 0 &&
            (isMac ? hotkeysEvent.key === 'Backspace' : ['Delete', 'Backspace'].includes(hotkeysEvent.key))
        ) {
            handleDelete(selectedNodes)
        }

        // 包裹到容器 (Shift + A)
        if (selectedNodes && selectedNodes.length > 0 && ['shift+a', 'shift+A'].includes(hotkeysEvent.key)) {
            handleWrapWithContainer(selectedNodes)
        }
    })

    const handleDelete = useAtomCallback((get, set, ids: NodeIdWithScope[]) => {
        actions.onRemove(ids)
    })

    // 找到所有选中blocks的公共父容器（支持混合选择普通页面和同步组件）
    const findCommonParent = useAtomCallback((get, set, selectedNodes: NodeIdWithScope[]) => {
        const getContainerState = (containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined
        const pageBlocks = get(blocksAtom)[pageId] || []
        const syncComponents = get(syncComponentsAtom)

        // 获取每个block的祖先路径（从根到该block的路径）
        function getAncestorPath(node: NodeIdWithScope): BlockAbstract[] {
            const { id, scope } = node

            // 如果有scope，说明在同步组件内
            if (scope) {
                // 1. 先在pageBlocks中找到scope对应的同步组件实例
                function findSyncInstance(blocks: BlockAbstract[], targetScope: string, path: BlockAbstract[]): BlockAbstract[] | null {
                    for (const block of blocks) {
                        // 找到同步组件实例（id匹配scope）
                        if (block.id === targetScope && block.synchronousId) {
                            return [...path, block]
                        }

                        const children = getCurrentBlockChildren(block, { getContainerState })
                        if (children) {
                            const result = findSyncInstance(children, targetScope, [...path, block])
                            if (result) {
                                return result
                            }
                        }
                    }
                    return null
                }

                const syncInstancePath = findSyncInstance(pageBlocks, scope, [])
                if (!syncInstancePath) {
                    return []
                }

                // 2. 在同步组件定义中找到目标block的路径
                function findInSyncComponent(
                    blocks: BlockAbstract[],
                    targetId: string,
                    currentScope: string,
                    path: BlockAbstract[]
                ): BlockAbstract[] | null {
                    for (const block of blocks) {
                        if (block.id === targetId) {
                            return [...path, block]
                        }

                        const children = getCurrentBlockChildren(block, {
                            getContainerState,
                            uniqueContainerId: block.isMasterSynchronous ? currentScope : `${currentScope}@${block.id}`
                        })

                        if (children) {
                            const result = findInSyncComponent(children, targetId, currentScope, [...path, block])
                            if (result) {
                                return result
                            }
                        }
                    }
                    return null
                }

                const innerPath = findInSyncComponent(syncComponents, id, scope, [])
                if (!innerPath) {
                    return []
                }

                // 合并路径：pageBlocks路径 + 同步组件内部路径
                return [...syncInstancePath, ...innerPath]
            }
            // 没有scope，直接在pageBlocks中查找
            function findPath(blocks: BlockAbstract[], targetId: string, path: BlockAbstract[]): BlockAbstract[] | null {
                for (const block of blocks) {
                    if (block.id === targetId) {
                        return [...path, block]
                    }

                    const children = getCurrentBlockChildren(block, { getContainerState })
                    if (children) {
                        const result = findPath(children, targetId, [...path, block])
                        if (result) {
                            return result
                        }
                    }
                }
                return null
            }

            return findPath(pageBlocks, id, []) || []
        }

        // 获取所有选中blocks的祖先路径
        const paths = selectedNodes.map(node => ({
            node,
            path: getAncestorPath(node)
        }))

        // 检查是否所有路径都找到了
        if (paths.some(p => p.path.length === 0)) {
            return null
        }

        // 找到最短路径长度
        const minLength = Math.min(...paths.map(p => p.path.length))

        // 找到公共父容器：从根开始比较，找到最后一个相同的祖先
        let commonParent: BlockAbstract | null = null
        let commonParentIndex = -1

        for (let i = 0; i < minLength - 1; i++) {
            const firstPath = paths[0].path[i]
            const allSame = paths.every(p => p.path[i]?.id === firstPath.id)

            if (allSame) {
                commonParent = firstPath
                commonParentIndex = i
            } else {
                break
            }
        }

        return { commonParent, paths, commonParentIndex }
    })

    // 将多个block包裹到容器中
    const handleWrapWithContainer = useAtomCallback(async (get, set, selectedNodes: NodeIdWithScope[]) => {
        if (!selectedNodes || selectedNodes.length === 0) {
            // Toast.error('请至少选择1个组件')
            return
        }

        const pageBlocks = get(blocksAtom)[pageId] || []
        const syncComponents = get(syncComponentsAtom)
        const blockFinder = createBlockFinder(pageBlocks, syncComponents)

        // 获取所有选中的blocks
        const selectedBlocks = selectedNodes.map(node => blockFinder.find(node)).filter(Boolean) as BlockAbstract[]

        if (selectedBlocks.length !== selectedNodes.length) {
            Toast.error('部分组件未找到')
            return
        }

        // 找到公共父容器（支持混合选择）
        const commonParentResult = findCommonParent(selectedNodes)

        if (!commonParentResult) {
            Toast.error('无法找到公共父容器')
            return
        }

        const { paths } = commonParentResult
        const getContainerState = (containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

        // 找到层级最浅（路径最短）的block
        const shallowestPath = paths.reduce((prev, curr) => (curr.path.length < prev.path.length ? curr : prev))
        const {
            node: { scope: shallowestNodeScope },
            path: shallowestPathArray
        } = shallowestPath
        const scope = shallowestNodeScope

        // 判断是否在同步组件内（最浅的node是否有scope）
        const isInSyncComponent = !!scope

        // 找到最浅block的直接父容器
        let targetParent: BlockAbstract | null = null
        let targetParentChildren: BlockAbstract[] = []

        if (shallowestPathArray.length === 1) {
            // 最浅block在根层级
            targetParent = null
            targetParentChildren = pageBlocks
        } else {
            // 最浅block有父容器
            targetParent = shallowestPathArray[shallowestPathArray.length - 2]
            const children = getCurrentBlockChildren(targetParent, {
                getContainerState,
                uniqueContainerId: scope ? (targetParent.isMasterSynchronous ? scope : `${scope}@${targetParent.id}`) : targetParent.id
            })
            if (!children) {
                Toast.error('无法获取父容器的子元素')
                return
            }
            targetParentChildren = children
        }

        // 找到最浅block在其父容器中的索引（新容器将在此位置创建）
        const {
            node: { id: shallowestNodeId }
        } = shallowestPath
        const shallowestBlockIndex = targetParentChildren.findIndex(b => b.id === shallowestNodeId)
        if (shallowestBlockIndex === -1) {
            Toast.error('未找到最浅层级组件的位置')
            return
        }

        if (isInSyncComponent) {
            // 在同步组件内处理
            await handleWrapInSyncComponent(selectedNodes, selectedBlocks, targetParent?.id, shallowestBlockIndex, scope)
        } else {
            // 在普通页面处理
            await handleWrapInPage(selectedNodes, selectedBlocks, targetParent?.id, shallowestBlockIndex)
        }
    })

    // 处理同步组件中的wrap操作（支持混合选择）
    const handleWrapInSyncComponent = useAtomCallback(
        async (
            get,
            set,
            selectedNodes: NodeIdWithScope[],
            selectedBlocks: BlockAbstract[],
            targetParentId: string | undefined,
            insertIndex: number,
            scope: string | undefined
        ) => {
            if (!scope) {
                Toast.error('同步组件scope不能为空')
                return
            }

            const getContainerState = (containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

            // 收集要移除和创建的信息
            const syncComponentsUndoRedoInfo: {
                removedBlocks: BlockAbstract[]
                prevBlocks?: ContainerBlockAbstract[]
                nextAfterRemove?: ContainerBlockAbstract[]
                nextAfterCreate?: ContainerBlockAbstract[]
                createdContainer?: ContainerBlockAbstract
            } = { removedBlocks: [] }

            const blocksUndoRedoInfo: {
                removedBlocks: BlockAbstract[]
                prevBlocks?: BlockAbstract[]
                nextAfterRemove?: BlockAbstract[]
                nextAfterCreate?: BlockAbstract[]
            } = { removedBlocks: [] }

            const blocksToAdd: BlockAbstract[] = []

            // 第一步：从同步组件中移除有scope的blocks
            set(syncComponentsAtom, draft => {
                syncComponentsUndoRedoInfo.prevBlocks = original(draft)

                selectedNodes.forEach(node => {
                    if (node.scope) {
                        function removeFromSync(blocks: BlockAbstract[], targetId: string): BlockAbstract | null {
                            for (let i = 0; i < blocks.length; i++) {
                                if (blocks[i].id === targetId) {
                                    return blocks.splice(i, 1)[0]
                                }
                                const children = getCurrentBlockChildren(blocks[i], {
                                    getContainerState,
                                    uniqueContainerId: blocks[i].isMasterSynchronous ? node.scope : `${node.scope}@${blocks[i].id}`
                                })
                                if (children) {
                                    const result = removeFromSync(children, targetId)
                                    if (result) {
                                        return result
                                    }
                                }
                            }
                            return null
                        }

                        const removed = removeFromSync(draft, node.id)
                        if (removed) {
                            const removedSnapshot = current(removed)
                            syncComponentsUndoRedoInfo.removedBlocks.push(removedSnapshot)
                            // 保持或转换同步特性
                            if (removed.isMasterSynchronous || removed.isLeafSynchronous) {
                                blocksToAdd.push(removedSnapshot)
                            } else {
                                blocksToAdd.push(transformBlock2SyncComponents(removedSnapshot, false))
                            }
                        }
                    }
                })

                syncComponentsUndoRedoInfo.nextAfterRemove = current(draft)
            })

            // 第二步：从普通页面blocks中移除无scope的blocks
            set(blocksAtom, draft => {
                const draftBlocks = draft[pageId]
                if (!draftBlocks) {
                    return
                }
                blocksUndoRedoInfo.prevBlocks = original(draftBlocks)

                selectedNodes.forEach(node => {
                    if (!node.scope) {
                        function removeFromPage(blocks: BlockAbstract[], targetId: string): BlockAbstract | null {
                            for (let i = 0; i < blocks.length; i++) {
                                if (blocks[i].id === targetId) {
                                    return blocks.splice(i, 1)[0]
                                }
                                const children = getCurrentBlockChildren(blocks[i], { getContainerState })
                                if (children) {
                                    const result = removeFromPage(children, targetId)
                                    if (result) {
                                        return result
                                    }
                                }
                            }
                            return null
                        }

                        const removed = removeFromPage(draftBlocks, node.id)
                        if (removed) {
                            const removedSnapshot = current(removed)
                            blocksUndoRedoInfo.removedBlocks.push(removedSnapshot)
                            // 转换为同步组件的子元素
                            blocksToAdd.push(transformBlock2SyncComponents(removedSnapshot, false))
                        }
                    }
                })

                blocksUndoRedoInfo.nextAfterRemove = current(draftBlocks)
            })

            // 第三步：创建新容器并添加到同步组件
            const newContainerConfig = generateContainerBlock(mainBreakPoint, DIRECTION.vertical)
            syncComponentsUndoRedoInfo.createdContainer = {
                id: `${BlockType.container}-${nanoid(12)}`,
                type: BlockType.container,
                title: '容器',
                config: newContainerConfig,
                children: [{ id: newContainerConfig.viewList[0].id, children: blocksToAdd }],
                isLeafSynchronous: true
            }

            set(syncComponentsAtom, draft => {
                // 在 draft 内部重新找到目标父容器的 children
                let targetChildren: BlockAbstract[]
                if (targetParentId) {
                    const parent = findBlockById(targetParentId, draft)
                    if (!parent) {
                        return
                    }
                    const children = getCurrentBlockChildren(parent, {
                        getContainerState,
                        uniqueContainerId: parent.isMasterSynchronous ? scope : `${scope}@${parent.id}`
                    })
                    if (!children) {
                        return
                    }
                    targetChildren = children
                } else {
                    // 根层级
                    targetChildren = draft
                }

                // 在目标位置插入新容器
                targetChildren.splice(insertIndex, 0, syncComponentsUndoRedoInfo.createdContainer!)
                set(blockCreatingListAtom, s => [...s, syncComponentsUndoRedoInfo.createdContainer!.id])

                syncComponentsUndoRedoInfo.nextAfterCreate = current(draft)
            })

            if (!syncComponentsUndoRedoInfo.createdContainer) {
                return
            }

            // 初始化容器运行时状态
            set(initContainerRuntimeStateAtom, {
                stackId,
                blocks: get(pageBlocksAtom(pageId)),
                syncComponents: syncComponentsUndoRedoInfo.nextAfterCreate!
            })

            // 设置选中状态
            set(pageInternalStateAtomFamily(stackId), draft => {
                draft.selectedNodes = [{ id: syncComponentsUndoRedoInfo.createdContainer!.id, scope }]
                draft.asideType = AsideType.BLOCK
            })

            // 判断是否跨类型移动
            const hasCrossTypeMove = blocksUndoRedoInfo.removedBlocks.length > 0

            // 第四步：调用API
            if (hasCrossTypeMove) {
                // 跨类型移动：删除普通block，创建同步组件
                console.log('跨类型移动：从普通组件删除')
                await removeBlock({
                    pageId,
                    stackId,
                    removedBlocks: blocksUndoRedoInfo.removedBlocks,
                    blocks: blocksUndoRedoInfo.nextAfterRemove!,
                    clearSelected: false
                })

                // 创建一个不包含children的容器副本
                const containerToCreate = {
                    ...syncComponentsUndoRedoInfo.createdContainer,
                    children: syncComponentsUndoRedoInfo.createdContainer.children.map(view => ({
                        ...view,
                        children: [] // 清空children
                    }))
                }

                // 从普通block转换为同步组件的blocks需要显式创建（递归创建它们的子级）
                const plainBlocksToCreate = blocksUndoRedoInfo.removedBlocks.map(block => transformBlock2SyncComponents(block, false))

                console.log('跨类型移动：创建同步组件容器和从普通block移入的blocks')
                await srv.createSyncBlock({
                    createBlocks: [containerToCreate, ...plainBlocksToCreate],
                    blocks: syncComponentsUndoRedoInfo.nextAfterCreate!
                })

                set(blockCreatingListAtom, s => s.filter(id => id !== syncComponentsUndoRedoInfo.createdContainer!.id))

                // 跨类型移动：只记录真正调用了API的删除和创建
                console.log('添加撤销重做记录（跨类型）')
                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        removed: {
                            // 只记录从普通组件中删除的blocks（调用了removeBlock）
                            removedBlocks: blocksUndoRedoInfo.removedBlocks,
                            prevBlocks: blocksUndoRedoInfo.prevBlocks,
                            nextBlocks: blocksUndoRedoInfo.nextAfterRemove
                            // 同步组件中的blocks是同类型移动，没有调用删除API，不记录
                        },
                        created: {
                            // 创建容器和从普通组件移入的blocks
                            createdSyncComponents: [containerToCreate, ...plainBlocksToCreate],
                            prevSyncComponents: syncComponentsUndoRedoInfo.prevBlocks, // 使用原始状态
                            nextSyncComponents: syncComponentsUndoRedoInfo.nextAfterCreate
                        }
                    }
                })
            } else {
                // 同类型移动：创建同步组件容器（不递归创建子元素，因为子元素是从同一模块移入的）
                console.log('同类型移动：创建同步组件容器')

                const containerToCreate = {
                    ...syncComponentsUndoRedoInfo.createdContainer,
                    children: syncComponentsUndoRedoInfo.createdContainer.children.map(view => ({
                        ...view,
                        children: [] // 清空children，子元素已在新结构中
                    }))
                }

                await srv.createSyncBlock({
                    createBlocks: [containerToCreate],
                    blocks: syncComponentsUndoRedoInfo.nextAfterCreate!
                })

                set(blockCreatingListAtom, s => s.filter(id => id !== syncComponentsUndoRedoInfo.createdContainer!.id))

                // 同类型移动：只记录创建（不记录removed，避免撤销时调用删除）
                console.log('添加撤销重做记录（同类型）')
                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        created: {
                            createdSyncComponents: [containerToCreate],
                            prevSyncComponents: syncComponentsUndoRedoInfo.prevBlocks,
                            nextSyncComponents: syncComponentsUndoRedoInfo.nextAfterCreate
                        }
                    }
                })
            }

            Toast.success('已创建容器并包裹选中组件')
        }
    )

    // 处理普通页面block的wrap操作（支持混合选择）
    const handleWrapInPage = useAtomCallback(
        async (
            get,
            set,
            selectedNodes: NodeIdWithScope[],
            selectedBlocks: BlockAbstract[],
            targetParentId: string | undefined,
            insertIndex: number
        ) => {
            const getContainerState = (containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

            // 收集要移除和创建的信息
            const blocksUndoRedoInfo: {
                removedBlocks: BlockAbstract[]
                prevBlocks?: BlockAbstract[]
                nextAfterRemove?: BlockAbstract[]
                nextAfterCreate?: BlockAbstract[]
                createdContainer?: ContainerBlockAbstract
            } = { removedBlocks: [] }

            const syncComponentsUndoRedoInfo: {
                removedBlocks: BlockAbstract[]
                prevBlocks?: ContainerBlockAbstract[]
                nextAfterRemove?: ContainerBlockAbstract[]
            } = { removedBlocks: [] }

            const blocksToAdd: BlockAbstract[] = []

            // 第一步：从普通页面blocks中移除无scope的blocks
            set(blocksAtom, draft => {
                const draftBlocks = draft[pageId]
                if (!draftBlocks) {
                    return
                }
                blocksUndoRedoInfo.prevBlocks = original(draftBlocks)

                selectedNodes.forEach(node => {
                    if (!node.scope) {
                        function removeFromPage(blocks: BlockAbstract[], targetId: string): BlockAbstract | null {
                            for (let i = 0; i < blocks.length; i++) {
                                if (blocks[i].id === targetId) {
                                    return blocks.splice(i, 1)[0]
                                }
                                const children = getCurrentBlockChildren(blocks[i], { getContainerState })
                                if (children) {
                                    const result = removeFromPage(children, targetId)
                                    if (result) {
                                        return result
                                    }
                                }
                            }
                            return null
                        }

                        const removed = removeFromPage(draftBlocks, node.id)
                        if (removed) {
                            const removedSnapshot = current(removed)
                            blocksUndoRedoInfo.removedBlocks.push(removedSnapshot)
                            // 直接添加（同步组件实例会保持其结构）
                            blocksToAdd.push(removedSnapshot)
                        }
                    }
                })

                blocksUndoRedoInfo.nextAfterRemove = current(draftBlocks)
            })

            // 第二步：从同步组件中移除有scope的blocks
            set(syncComponentsAtom, draft => {
                syncComponentsUndoRedoInfo.prevBlocks = original(draft)

                selectedNodes.forEach(node => {
                    if (node.scope) {
                        function removeFromSync(blocks: BlockAbstract[], targetId: string): BlockAbstract | null {
                            for (let i = 0; i < blocks.length; i++) {
                                if (blocks[i].id === targetId) {
                                    return blocks.splice(i, 1)[0]
                                }
                                const children = getCurrentBlockChildren(blocks[i], {
                                    getContainerState,
                                    uniqueContainerId: blocks[i].isMasterSynchronous ? node.scope : `${node.scope}@${blocks[i].id}`
                                })
                                if (children) {
                                    const result = removeFromSync(children, targetId)
                                    if (result) {
                                        return result
                                    }
                                }
                            }
                            return null
                        }

                        const removed = removeFromSync(draft, node.id)
                        if (removed) {
                            const removedSnapshot = current(removed)
                            syncComponentsUndoRedoInfo.removedBlocks.push(removedSnapshot)
                            // 同步组件block转为普通block（递归去掉同步标识）
                            const plainBlock = clone(removedSnapshot)
                            deepClearSyncBlock(plainBlock)
                            blocksToAdd.push(plainBlock)
                        }
                    }
                })

                syncComponentsUndoRedoInfo.nextAfterRemove = current(draft)
            })

            // 第三步：创建新容器并添加到普通页面
            const newContainerConfig = generateContainerBlock(mainBreakPoint, DIRECTION.vertical)
            blocksUndoRedoInfo.createdContainer = {
                id: `${BlockType.container}-${nanoid(12)}`,
                type: BlockType.container,
                title: '容器',
                config: newContainerConfig,
                children: [{ id: newContainerConfig.viewList[0].id, children: blocksToAdd }]
            }

            set(blocksAtom, draft => {
                const draftBlocks = draft[pageId]
                if (!draftBlocks) {
                    return
                }

                // 在 draft 内部重新找到目标父容器的 children
                let targetChildren: BlockAbstract[]
                if (targetParentId) {
                    const parent = findBlockById(targetParentId, draftBlocks)
                    if (!parent) {
                        return
                    }
                    const children = getCurrentBlockChildren(parent, { getContainerState })
                    if (!children) {
                        return
                    }
                    targetChildren = children
                } else {
                    // 根层级
                    targetChildren = draftBlocks
                }

                // 在目标位置插入新容器
                targetChildren.splice(insertIndex, 0, blocksUndoRedoInfo.createdContainer!)
                set(blockCreatingListAtom, s => [...s, blocksUndoRedoInfo.createdContainer!.id])

                blocksUndoRedoInfo.nextAfterCreate = current(draftBlocks)
            })

            if (!blocksUndoRedoInfo.createdContainer) {
                return
            }

            // 设置选中状态
            set(pageInternalStateAtomFamily(stackId), draft => {
                draft.selectedNodes = [{ id: blocksUndoRedoInfo.createdContainer!.id, scope: undefined }]
                draft.asideType = AsideType.BLOCK
            })

            // 判断是否跨类型移动
            const hasCrossTypeMove = syncComponentsUndoRedoInfo.removedBlocks.length > 0

            // 第四步：调用API
            if (hasCrossTypeMove) {
                // 跨类型移动：删除同步组件，创建普通block
                console.log('跨类型移动：从同步组件删除')
                await srv.deleteSyncLeafBlocks({
                    pageId,
                    removedBlocks: syncComponentsUndoRedoInfo.removedBlocks,
                    blocks: syncComponentsUndoRedoInfo.nextAfterRemove!
                })

                // 创建一个不包含children的容器副本
                const containerToCreate = {
                    ...blocksUndoRedoInfo.createdContainer,
                    children: blocksUndoRedoInfo.createdContainer.children.map(view => ({
                        ...view,
                        children: [] // 清空children
                    }))
                }

                // 从同步组件移出来的blocks需要显式创建（递归创建它们的子级）
                const syncBlocksToCreate = syncComponentsUndoRedoInfo.removedBlocks.map(block => {
                    const plainBlock = clone(block)
                    deepClearSyncBlock(plainBlock)
                    return plainBlock
                })

                console.log('跨类型移动：创建普通容器和从同步组件移入的blocks')
                await createBlock({
                    pageId,
                    stackId,
                    createdBlocks: [containerToCreate, ...syncBlocksToCreate],
                    blocks: blocksUndoRedoInfo.nextAfterCreate!
                })

                set(blockCreatingListAtom, s => s.filter(id => id !== blocksUndoRedoInfo.createdContainer!.id))

                // 跨类型移动：只记录真正调用了API的删除和创建
                console.log('添加撤销重做记录（跨类型）')
                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        removed: {
                            // 只记录从同步组件中删除的blocks（调用了deleteSyncLeafBlocks）
                            removedSyncComponents: syncComponentsUndoRedoInfo.removedBlocks,
                            prevSyncComponents: syncComponentsUndoRedoInfo.prevBlocks,
                            nextSyncComponents: syncComponentsUndoRedoInfo.nextAfterRemove
                            // 普通组件中的blocks是同类型移动，没有调用删除API，不记录
                        },
                        created: {
                            // 创建容器和从同步组件移入的blocks
                            createdBlocks: [containerToCreate, ...syncBlocksToCreate],
                            prevBlocks: blocksUndoRedoInfo.prevBlocks, // 使用原始状态
                            nextBlocks: blocksUndoRedoInfo.nextAfterCreate
                        }
                    }
                })
            } else {
                // 同类型移动：创建普通容器（不递归创建子元素，因为子元素是从同一模块移入的）
                console.log('同类型移动：创建普通容器')

                const containerToCreate = {
                    ...blocksUndoRedoInfo.createdContainer,
                    children: blocksUndoRedoInfo.createdContainer.children.map(view => ({
                        ...view,
                        children: [] // 清空children，子元素已在新结构中
                    }))
                }

                await createBlock({
                    pageId,
                    stackId,
                    createdBlocks: [containerToCreate],
                    blocks: blocksUndoRedoInfo.nextAfterCreate!
                })

                set(blockCreatingListAtom, s => s.filter(id => id !== blocksUndoRedoInfo.createdContainer!.id))

                // 同类型移动：只记录创建（不记录removed，避免撤销时调用删除）
                console.log('添加撤销重做记录（同类型）')
                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        created: {
                            createdBlocks: [containerToCreate],
                            prevBlocks: blocksUndoRedoInfo.prevBlocks,
                            nextBlocks: blocksUndoRedoInfo.nextAfterCreate
                        }
                    }
                })
            }

            Toast.success('已创建容器并包裹选中组件')
        }
    )

    useHotkeys('ctrl+c, ⌘+c, ctrl+x, ⌘+x, ctrl+v, ⌘+v, Delete, Backspace, shift+a', handleKeydown, { enabled }, [enabled])
}

function checkNotAllowed({ fromBlock, toBlock, blocks }: { fromBlock: BlockAbstract; toBlock: BlockAbstract; blocks: BlockAbstract[] }) {
    // 表单容器、视图及图表block禁止插入到自定义视图内
    if (
        includeNotAllowedBlock(fromBlock, {
            notAllowedList: [BlockType.view, BlockType.chart],
            viewTypeFilter: block => block.type === BlockType.view && block.config?.viewType === 'custom'
        }) &&
        ((toBlock && isCustomViewBlock(toBlock)) || isCustomChildren(toBlock.id, blocks))
    ) {
        Toast.error('自定义视图不允许拖入自定义视图')
        return false
    }
    // 表单容器、视图及图表block禁止插入到弹出窗口内
    // if (
    //     includeNotAllowedBlock(fromBlock, [BlockType.formContainer, BlockType.view, BlockType.chart]) &&
    //     toBlock &&
    //     isFloatBoxBlock(toBlock)
    // ) {
    //     Toast.error('视图、图表、表单容器，不允许拖入弹出窗口')
    //     return
    // }

    if (
        includeNotAllowedBlock(fromBlock, { notAllowedList: [BlockType.subForm] }) &&
        ((toBlock && !isFormContainerBlock(toBlock)) || !isFormContainerChildren(toBlock.id, blocks))
    ) {
        Toast.error('子表单只能拖入到表单容器中')
        return false
    }

    if (includeNotAllowedBlock(fromBlock, { notAllowedList: [BlockType.formContainer] }) && toBlock && isFormContainerBlock(toBlock)) {
        Toast.error('表单容器不允许拖入表单容器')
        return
    }
    return true
}
